import React, { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Star,
  Loader2,
  User2,
  MessageSquare,
  Trash2,
  Filter,
  CheckCircle2,
  Download,
  Calendar,
  ExternalLink,
} from 'lucide-react'
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
} from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useNavigate } from 'react-router-dom'

export default function RatingsPage() {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [status, setStatus] = useState('idle')
  const [log, setLog] = useState([])

  // 🔹 Filter
  const [filterType, setFilterType] = useState('all')
  const [filterRating, setFilterRating] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const navigate = useNavigate()

  // ============================================================
  // 🔹 LOAD DATA DARI FIRESTORE
  // ============================================================
  useEffect(() => {
    const q = query(collection(db, 'ulasan'), orderBy('tanggal', 'desc'))
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }))
        setReviews(list)
        setLoading(false)
      },
      (err) => {
        console.error('❌ Error memuat ulasan:', err)
        setLoading(false)
      }
    )
    return () => unsubscribe()
  }, [])

  // ============================================================
  // 🔹 FILTER DATA
  // ============================================================
  const filtered = reviews.filter((r) => {
    if (filterType !== 'all' && r.mitraType !== filterType) return false
    if (filterRating !== 'all') {
      if (filterRating === '3' && r.rating < 3) return false
      if (filterRating === '4' && r.rating < 4) return false
      if (filterRating === '5' && r.rating !== 5) return false
    }

    if (startDate) {
      const start = new Date(startDate).getTime()
      const t = r.tanggal?.toDate ? r.tanggal.toDate().getTime() : 0
      if (t < start) return false
    }

    if (endDate) {
      const end = new Date(endDate).getTime() + 86400000
      const t = r.tanggal?.toDate ? r.tanggal.toDate().getTime() : 0
      if (t > end) return false
    }

    return true
  })

  // ============================================================
  // 🔹 TOGGLE CHECKBOX
  // ============================================================
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    if (selected.length === filtered.length) setSelected([])
    else setSelected(filtered.map((r) => r.id))
  }

  // ============================================================
  // 🔹 HAPUS ULASAN
  // ============================================================
  const handleDeleteSelected = async () => {
    if (selected.length === 0) {
      alert('Pilih minimal satu ulasan untuk dihapus.')
      return
    }

    const confirmDelete = window.confirm(
      `Yakin ingin menghapus ${selected.length} ulasan yang dipilih?`
    )
    if (!confirmDelete) return

    setStatus('running')
    setLog([])

    try {
      const toDelete = reviews.filter((r) => selected.includes(r.id))
      await Promise.all(toDelete.map((r) => deleteDoc(r.ref)))

      setSelected([])
      setStatus('done')
      setLog([`✅ ${toDelete.length} ulasan berhasil dihapus.`])
    } catch (err) {
      console.error('Error deleting reviews:', err)
      setStatus('error')
      setLog(['❌ Terjadi kesalahan: ' + err.message])
    }
  }

  // ============================================================
  // 🔹 EKSPOR CSV
  // ============================================================
  const handleExportCSV = () => {
    if (filtered.length === 0) {
      alert('Tidak ada data untuk diekspor.')
      return
    }

    const headers = [
      'User Name',
      'Komentar',
      'Mitra ID',
      'Mitra Type',
      'Rating',
      'Tanggal',
    ]
    const rows = filtered.map((r) => [
      r.userName || 'Anonim',
      `"${r.komentar || ''}"`,
      r.mitraId || '-',
      r.mitraType || '-',
      r.rating || '-',
      r.tanggal?.toDate
        ? new Date(r.tanggal.toDate()).toLocaleString('id-ID')
        : '-',
    ])

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute(
      'download',
      `ulasan_zimo_${new Date().toISOString().split('T')[0]}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // ============================================================
  // 🔹 NAVIGATE KE DETAIL MITRA
  // ============================================================
  const goToMitraDetail = (type, id) => {
    if (!type || !id) return
    if (type === 'cleaner') navigate(`/cleaners/${id}`)
    else if (type === 'therapist') navigate(`/therapists/${id}`)
    else alert('Tipe mitra tidak dikenal.')
  }

  // ============================================================
  // 🔹 UI UTAMA
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-blue-400 hover:text-blue-500 transition"
          >
            <ArrowLeft size={18} /> Kembali
          </button>
          <h2 className="text-xl font-semibold flex items-center gap-2 text-blue-400">
            <Star size={20} /> Daftar Rating & Ulasan Mitra
          </h2>
        </div>

        {/* FILTER */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Filter size={16} className="text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-gray-800 text-sm border border-gray-700 rounded-md px-2 py-1 text-gray-200"
          >
            <option value="all">Semua Mitra</option>
            <option value="cleaner">Cleaner</option>
            <option value="therapist">Therapist</option>
          </select>

          <select
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            className="bg-gray-800 text-sm border border-gray-700 rounded-md px-2 py-1 text-gray-200"
          >
            <option value="all">Semua Rating</option>
            <option value="3">≥ 3</option>
            <option value="4">≥ 4</option>
            <option value="5">= 5</option>
          </select>

          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-800 text-sm border border-gray-700 rounded-md px-2 py-1 text-gray-200"
            />
            <span className="text-gray-400">s/d</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-800 text-sm border border-gray-700 rounded-md px-2 py-1 text-gray-200"
            />
          </div>

          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium"
          >
            <Download size={14} /> Ekspor CSV
          </button>

          {selected.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium"
            >
              <Trash2 size={14} /> Hapus ({selected.length})
            </button>
          )}
        </div>
      </div>

      {/* DAFTAR ULASAN */}
      {loading ? (
        <div className="flex justify-center items-center py-20 text-gray-400">
          <Loader2 size={18} className="animate-spin mr-2" />
          Memuat data ulasan...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-10">
          Tidak ada ulasan ditemukan untuk filter ini.
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-lg divide-y divide-gray-700">
          <div className="flex items-center justify-between p-2 bg-gray-850 text-sm text-gray-300 font-medium border-b border-gray-700">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.length === filtered.length}
                onChange={selectAll}
              />
              <span>Ulasan ({filtered.length})</span>
            </div>
          </div>

          {filtered.map((r) => (
            <div
              key={r.id}
              className="p-4 hover:bg-gray-850 transition-colors duration-150 flex items-start gap-3"
            >
              <input
                type="checkbox"
                checked={selected.includes(r.id)}
                onChange={() => toggleSelect(r.id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <User2 size={16} className="text-blue-400" />
                    <div>
                      <h3 className="font-semibold text-white text-sm">
                        {r.userName || 'Pengguna Anonim'}
                      </h3>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        {r.mitraType?.toUpperCase() || 'UNKNOWN'} •{' '}
                        <button
                          onClick={() =>
                            goToMitraDetail(r.mitraType, r.mitraId)
                          }
                          className="text-blue-400 hover:underline flex items-center gap-1"
                        >
                          {r.mitraId} <ExternalLink size={12} />
                        </button>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-yellow-400">
                    {Array.from({ length: Math.floor(r.rating || 0) }).map(
                      (_, i) => (
                        <Star key={i} size={14} fill="#FFD700" />
                      )
                    )}
                    <span className="text-xs text-gray-300 ml-1">
                      {r.rating?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                </div>

                <p className="text-gray-300 text-sm mt-2 flex items-start gap-1">
                  <MessageSquare size={14} className="text-blue-400 mt-[2px]" />
                  {r.komentar || 'Tidak ada komentar.'}
                </p>

                <p className="text-xs text-gray-500 mt-2">
                  {r.tanggal?.toDate
                    ? new Date(r.tanggal.toDate()).toLocaleString('id-ID', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LOG */}
      {log.length > 0 && (
        <div className="mt-5 p-3 bg-gray-800 border border-gray-700 rounded-md text-sm text-gray-300 max-h-64 overflow-y-auto">
          {log.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      )}

      {status === 'done' && (
        <div className="flex items-center gap-2 text-green-400 mt-4 text-sm">
          <CheckCircle2 size={16} />
          {log[0]}
        </div>
      )}
    </div>
  )
}
